import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Text } from '@/components/ui/Text'
import { CreditIcon } from '@/components/icons/CreditIcon'
import { PRIVATE_CREDIT_RATES } from '@/lib/billing/creditRateTable'
import { useTranslation } from 'react-i18next'

const CREDIT_RATE_KEYS: Record<string, { action: string; unit: string }> = {
  'Audience creation': {
    action: 'billing.plans.rates.audienceCreation',
    unit: 'billing.plans.rates.perTwin',
  },
  Study: {
    action: 'billing.plans.rates.study',
    unit: 'billing.plans.rates.perRespondent',
  },
  'Quick study': {
    action: 'billing.plans.rates.quickStudy',
    unit: 'billing.plans.rates.perRespondentPerQuestion',
  },
  Chat: {
    action: 'billing.plans.rates.chat',
    unit: 'billing.plans.rates.perMessage',
  },
  'Focus group': {
    action: 'billing.plans.rates.focusGroup',
    unit: 'billing.plans.rates.perMessage',
  },
}

export interface HowCreditsWorkDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

/**
 * Standard private-audience rates. Audience-specific overrides stay off this
 * table: a custom rate belongs to one audience, and this dialog is the
 * default card every customer sees.
 */
export function HowCreditsWorkDialog(props: HowCreditsWorkDialogProps) {
  const { open, onOpenChange } = props
  const { t } = useTranslation('app')

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-3xl gap-0 p-0"
        data-testid="how-credits-work-dialog"
      >
        <div className="flex flex-col gap-6 px-6 pb-8 pt-6 sm:px-10 sm:pb-10">
          <DialogHeader className="space-y-2 text-left sm:text-left">
            <DialogTitle className="text-2xl font-normal tracking-tight">
              {t('billing.plans.howCreditsWork')}
            </DialogTitle>
            <DialogDescription className="text-sm text-muted-foreground leading-relaxed">
              {t('billing.plans.howCreditsBody')}
            </DialogDescription>
          </DialogHeader>

          <div className="overflow-x-auto rounded-lg border border-border">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border bg-muted/50">
                  <th className="w-1/2 px-5 py-3 text-left text-xs font-medium text-foreground">
                    {t('billing.plans.action')}
                  </th>
                  <th className="px-5 py-3 text-left text-xs font-medium text-foreground">
                    {t('billing.plans.credits')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {PRIVATE_CREDIT_RATES.map((row) => {
                  const keys = CREDIT_RATE_KEYS[row.action]
                  return (
                    <tr key={row.action} className="border-b border-border last:border-0">
                      <td className="px-5 py-3 text-sm text-foreground">
                        {keys ? t(keys.action as 'billing.plans.rates.study') : row.action}
                      </td>
                      <td className="px-5 py-3">
                        <span className="inline-flex items-center gap-1 tabular-nums text-sm text-foreground">
                          <CreditIcon className="h-3 w-3 text-muted-foreground" />
                          {row.rate}
                        </span>
                        <div className="text-xs font-normal text-muted-foreground">
                          {keys ? t(keys.unit as 'billing.plans.rates.perTwin') : row.unit}
                        </div>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>

          <Text size="sm" color="muted" className="leading-relaxed">
            {t('billing.plans.marketplaceFootnote')}
          </Text>
        </div>
      </DialogContent>
    </Dialog>
  )
}
